import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const staggeredListRevealSkill: CodeoSkill = {
    name: SKILL_NAMES.staggeredListReveal,
    kind: 'codeo',
    description:
        'Reveal a group of related items (list rows, grid cards, stat chips) with a stagger so ' +
        'the group reads as one gesture instead of simultaneous pops or one slow queue.',
    documentUsesSkill: check((script, markup) => /stagger/.test(script) && repeatsAClass(markup)),
    instructions: `# Staggered List Reveal

A group of sibling items enters as one gesture: same motion, offset starts. The stagger interval is the craft — too tight reads as a single pop, too loose reads as a slideshow queue.

## Markup

Each row is its own text leaf with a unique \`id\`. Keep the rows tweened by a shared class (\`.item\`) and put no \`transform\` in their CSS.

\`\`\`html
<div id="benefits" style="position: absolute; left: 160px; top: 380px; display: flex; flex-direction: column; gap: 20px; font-family: var(--l5-font-1), sans-serif; font-size: 52px; color: var(--l5-color-0, #101820);">
  <p class="item" id="benefit-speed">Faster cuts</p>
  <p class="item" id="benefit-cost">Lower cost</p>
  <p class="item" id="benefit-reach">Wider reach</p>
</div>
\`\`\`

## Timeline code

One paused timeline, registered at \`window.__timelines.main\`. A single \`from\` with \`stagger\` gives every row the same motion at offset starts:

\`\`\`js
const tl = gsap.timeline({ paused: true });
tl.from('#benefits .item',
    { opacity: 0, y: 28, duration: 0.55, ease: 'power3.out', stagger: 0.14 },
    0.2);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- \`stagger: 0.1–0.18\` for 3–5 items. For more items, keep the TOTAL spread under ~0.7s: \`stagger: { each: 0.7 / count }\`.
- Direction: \`stagger: { each: 0.14, from: 'end' }\` reveals bottom-up; \`from: 'center'\` blooms outward — pick what matches the layout's reading order.
- Horizontal rows: slide from \`x: 28\` instead of \`y\`.
- If the narration names each item, abandon the fixed stagger and place one tween per item at its spoken moment. The scene's \`cues:\` line prefixes every word with \`[N.Ns]\`; position each item's tween at its word's second directly (see ${SKILL_NAMES.kineticWordReveal} for the per-cue pattern) — voice-timed beats uniform.

## Pitfalls

- The hidden from-state (\`opacity: 0\`) makes default \`immediateRender\` correct — items stay hidden until the group enters. Don't add \`immediateRender: false\`, and don't also set CSS \`opacity: 0\` on \`.item\`.
- Items are GSAP-tweened — no \`transform\` in \`.item\` CSS (a competing CSS transform on a tweened channel makes the element jump).
- Each row holds plain text under a unique \`id\` — don't split a row into inner spans.
- Keep item text short (1–4 words). A staggered entrance buys you nothing if each row is a sentence the viewer can't finish.
- At 52px on a 1080-tall frame the rows clear the readable-size floor comfortably; don't drop list copy below ~2.5% of canvas height.`,
};
